import Reflux from "reflux";
import Immutable, { List, Map, fromJS } from "immutable";
import _ from "lodash";
import { Big } from "big.js";

import AppConstants from "../constants/AppConstants";
import { PAGINATED_TEMPLATE_TYPES } from "../constants/WP";
import SelectionStore from "./SelectionStore";
import SiteDesign from "../entities/SiteDesign";
import SitePage from "../entities/SitePage";
import SiteActions from "../actions/SiteActions";
import UIActions from "../actions/UIActions";
import AssetActions from "../actions/AssetActions";
import AppActions from "../actions/AppActions";
import DesignPreparer from "../managers/DesignPreparer";
import DesignManager from "../managers/DesignManager";
import DesignFile from "../managers/DesignFile";
import UndoRedoManager from "../managers/UndoRedoManager";
import API from "../service/API";
import ModelFactory from "../models/ModelFactory";
import BlockUtils from "../utils/BlockUtils";
import IDUtils from "../utils/IDUtils";
import WordpressRemover from "../utils/WordpressRemover";
import AdminActions from "../actions/AdminActions";
import UserActions from "../actions/UserActions";
import AppConfig from "../config/app";
import BlockUpdateVO from "../vo/BlockUpdate.mjs";
import SiteService from "../service/SiteService.mjs";
import { applyThemeBG } from "../utils/SyncUtils";
import DesignViewStore from "./DesignViewStore";
import PageCleanupUtils from "../utils/PageCleanupUtils";
import { ITEM_HISTORY_ID_INDEX, ITEM_HISTORY_TYPE_INDEX } from "../constants/DesignViewConstants";
import { isCorrectVersion } from "../utils/DesignVersionUtils";
import { isIOS } from "@/constants/BrowserConstants";
import {
  CANVAS_SET_DATA,
  EXCLUDE_FROM_GLOBAL_CODE,
  GLOBAL_CODE_DATA_KEY,
  GLOBAL_CODE_KEY,
  LAYOUT_DESKTOP,
} from "../constants/DesignConstants.mjs";
import DesignUpdate from "../vo/DesignUpdate";
import { buildRecentPexelsPhotosUpdate } from "../utils/PexelsRecentsUtils";
import { createDefaultButtonStyles, removeHiddenFormStyleProps } from "../utils/ThemeUtils";
import { resetFormFieldStateCopies } from "../utils/FormFieldStateReset";
import {
  isV2FeatureEnabled,
  whenV2FeatureEnabled,
} from "../utils/FeatureFlagUtils.mjs";
import { FORMS_FLAG } from "../constants/FeatureFlags.mjs";
import DataTypes, { GRAPHIC } from "@/constants/DataTypes";
import { isPropSynced, getProp } from "@/utils/Merge/MergeUtils.mjs";
import {
  GRAPHIC_STYLE_HORIZONTAL_SHIFT,
  GRAPHIC_STYLE_OFFSET_X,
  GRAPHIC_STYLE_OFFSET_Y,
  GRAPHIC_STYLE_SCALE_PERCENTAGE,
  GRAPHIC_STYLE_TYPE,
} from "@/constants/MergeConstants";
import BlockDuplicationUtils from "../utils/BlockDuplicateUtils";
import GroupActions from "@/actions/GroupActions";
import { ELEMENTS } from "@/constants/EntityConstants";
import GroupStore from "./GroupStore";
import { DROP_AREA_CENTER, DROP_AREA_TOP } from "@/constants/LayerConstants";

const {
  BLOCK_PROP_PARENT_BLOCK_ID,
  CANVAS_SET_CHILDREN,
  DESIGN_TYPE_BLOCK,
  DESIGN_TYPE_PAGE,
  DESIGN_TYPE_SITE_BLOCK,
  DESIGN_TYPE_WP_PAGE,
  ELEMENT_TYPE_HTML_TEXT,
  ELEMENT_TYPE_TEXT,
  INPUT_SOURCE_PROPERTY_INPUT,
  ITEM_TYPE_BLOCK,
  ITEM_TYPE_CANVAS_SET,
  ITEM_TYPE_CANVAS_SET_BLOCK,
  ITEM_TYPE_ELEMENT,
  ITEM_TYPE_GROUP,
  ITEM_TYPE_PAGE,
  ITEM_TYPE_PAGE_FOLDER,
  ITEM_TYPE_SITE_BLOCK,
  ITEM_TYPE_STATE,
  LAYOUT_MOBILE,
  LOCK_EDITING_KEY,
  PANEL_LAYERS,
  SITE_INIT_PAGE_KEY,
  SITE_ITEM_PAGE,
  SITE_ITEM_SITE,
  SITE_ITEM_WP_PAGE,
  CANVAS_SET_BLOCK_CONTENT_SEPARATOR,
} = AppConstants;

/**
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS[number]} Layout
 * @typedef {import("@/types/Selection.mjs").ElementInfo} ElementSelectionInfo
 * @typedef {import("@/entities/Block").default} Block
 * @typedef {import("@/entities/BlockContainer").default} BlockContainer
 */

const selectedModel = {
  // Selection resets to this layout on load/clear; on touch it matches the
  // desktop-only stage view
  layout: isIOS ? LAYOUT_DESKTOP : LAYOUT_MOBILE,
  blockId: null,
  canvasSetId: null,
  blockState: null,
  elements: {},
  from: null,
  lastElement: null,
};

/**
 * This store contains the contents of the site.json file
 * Any changes to the site should be put here
 */
const SiteDesignStore = Reflux.createStore({
  storeName: 'SiteDesignStore',
  listenables: [SiteActions, UIActions, AssetActions, AdminActions],

  init() {
    UndoRedoManager.addStore("SiteDesignStore", this);

    // these properties are the source of truth everything else is derived from them.
    this.siteDesign = new SiteDesign(Immutable.Map()); // SiteDesign Entity
    this.sitePage = new SitePage(Immutable.Map()); // SitePage Entity

    this.selected = Immutable.fromJS(selectedModel);

    this.blockContainerType = null;

    this.id = null; // Debug: this is key?

    this.pageList = Immutable.List();
    this.fullPageList = Immutable.List();
    this.siteBlockList = Immutable.List();
    this.canvasSetList = Immutable.List();

    this.initPage = false;

    this.loadingPage = false;

    // Track pages pending save for cleanup on failure
    this.pagesPendingSave = new Set();

    this.loadingPageInfo = null;
    this.initialPageLoad = false;

    // Flag to prevent selection race conditions during duplication
    this.isDuplicating = false;
  },

  onSetDesign(data) {
    if (data == null || !_.isObject(data.design)) {
      this.reset();
      return;
    }

    // A design is currently loaded but should be cleared out before loading a new one.
    if (this.id != null) {
      DesignManager.reset();
      UndoRedoManager.reset();

      // Clear pending page tracking when loading new design
      this.pagesPendingSave.clear();

      this.initPage = false;
      this.initialPageLoad = false;
      this.loadingPageInfo = null;
    }

    const [designData, dirty] = this._prepDesignData(data.design);

    this.siteDesign = new SiteDesign(Immutable.fromJS(designData));
    this.id = this.siteDesign.id;

    const siteFile = new DesignFile({
      data: this.siteDesign.data,
      siteId: this.id,
      saveFunc: API.saveDesignFile,
      eTag: data.eTag,
      inSync: !dirty,
    });

    DesignManager.addFile(siteFile);

    // dirty if design contained old data that was then removed on design load
    if (dirty) {
      DesignManager.saveNow();
    }
    this._updateLists();

    // set container type to page since that will be selected initially
    this.blockContainerType = SITE_ITEM_PAGE;

    const initPageId = _.get(data, "pageId", false);

    if (initPageId) {
      const initPage = this.siteDesign.getPageItem(initPageId);
      if (initPage) {
        this.loadingPageInfo = initPage;
      }
    }

    const homePage = this.siteDesign.getHomePage();

    if (this.loadingPageInfo == null && !_.isEmpty(homePage)) {
      this.loadingPageInfo = homePage;
    } else {
      this.initPage = true;
    }

    if (this.loadingPageInfo) {
      this.initialPageLoad = true;
      SiteActions.loadPage({
        designId: this.id,
        pageId: this.loadingPageInfo.get("id"),
      });
    }

    this.selected = Immutable.fromJS(selectedModel);

    if (this._isInitComplete()) {
      this.trigger();
    }

    this._watchFormStylesGate();
  },

  _watchFormStylesGate() {
    this.stopWatchingFormStylesGate?.();
    this.stopWatchingFormStylesGate = null;

    if (isV2FeatureEnabled(FORMS_FLAG)) {
      return;
    }

    const designId = this.id;

    this.stopWatchingFormStylesGate = whenV2FeatureEnabled(FORMS_FLAG, () => {
      if (this.id === designId) {
        this._seedDefaultFormStyles();
      }
    });
  },

  _seedDefaultFormStyles() {
    const theme = this.siteDesign?.theme?.toJS();
    if (!theme) {
      return;
    }

    const [seededTheme, seedDirty] = DesignPreparer.addMissingFormStyles(theme);
    // Temporary: remove with the reset. Any form styles that existed at load were already reset
    // and marked there, so this only ever marks a fresh seed.
    const [resetTheme, resetDirty] = resetFormFieldStateCopies(seededTheme);
    if (!seedDirty && !resetDirty) {
      return;
    }

    this.siteDesign.theme = Immutable.fromJS(resetTheme);

    this._updateDesignFiles();

    if (this._isInitComplete()) {
      this.trigger();
    }
  },

  /**
   * Checks design for certain properties that may be missing and adds them in
   * @param {SiteDesign} design
   * @return an object containing the cleaned data
   */
  _prepDesignData(design) {
    let dirty = DesignPreparer.prepareDesignItem(design, design)[1];
    const [cleanedBlock] = DesignPreparer.cleanBlockContainer(design);
    if (!cleanedBlock?.blocks) {
      cleanedBlock.blocks = [];
    }

    if (!cleanedBlock?.blockData) {
      cleanedBlock.blockData = {};
    }
    if (cleanedBlock?.theme && !cleanedBlock.theme?.styles.primary) {
      cleanedBlock.theme = createDefaultButtonStyles(cleanedBlock.theme);
      dirty = true;
    }

    if (cleanedBlock?.theme && isV2FeatureEnabled(FORMS_FLAG)) {
      const [theme, themeDirty] = DesignPreparer.addMissingFormStyles(cleanedBlock.theme);
      cleanedBlock.theme = theme;
      if (themeDirty) {
        dirty = true;
      }
    }

    // Runs whatever the forms gate says, before any undo snapshot, so an undo can't bring a
    // removed hidden prop back.
    if (cleanedBlock?.theme) {
      const [hiddenTheme, hiddenDirty] = removeHiddenFormStyleProps(cleanedBlock.theme);
      cleanedBlock.theme = hiddenTheme;
      if (hiddenDirty) {
        dirty = true;
      }
    }

    // Temporary: cleans Focus/Error copies left by the pre-868mkgxmj seed. Remove once
    // early-access designs have loaded. Runs whatever the forms gate says, so it lands here,
    // before any undo snapshot, and an undo can't bring the copies back.
    if (cleanedBlock?.theme) {
      const [resetTheme, resetDirty] = resetFormFieldStateCopies(cleanedBlock.theme);
      cleanedBlock.theme = resetTheme;
      if (resetDirty) {
        dirty = true;
      }
    }

    const [cleanedSiteBlocks, siteBlocksDirty] = DesignPreparer.cleanSiteBlockData(cleanedBlock);
    if (siteBlocksDirty) {
      dirty = true;
    }

    return [DesignPreparer.cleanCanvasSetData(cleanedSiteBlocks), dirty];
  },

  onSelectPage(data) {
    const pageToLoad = data.page;
    const currentContainerId = this.sitePage.id;
    const pageId = pageToLoad.get("id");

    UIActions.clearSelected();
    GroupStore.isFocused() && GroupActions.blurGroup();

    if (pageId !== currentContainerId || !this._isBlockContainerType(SITE_ITEM_PAGE)) {
      this.blockContainerType = SITE_ITEM_PAGE;

      // if this page is currently being managed use the data in memory instead of fetching
      const designFile = DesignManager.getFile(pageId);

      if (designFile) {
        // going to set a delay to allow any current updates for the existing page to apply before loading the new page
        setTimeout(() => {
          this._loadPage({ id: pageId });
          this.trigger();
        }, 250);
      } else {
        this.loadingPageInfo = pageToLoad;
        SiteActions.loadPage({
          designId: this.id,
          pageId,
        });
      }
    }
  },

  onSelectSiteBlock(data) {
    const { block } = data;
    const blockId = block.get("id");

    const siteBlock = this.siteDesign.getBlockEntity(blockId);
    if (siteBlock) {
      this._resetSelection();

      this.blockContainerType = SITE_ITEM_SITE;

      let blockState = null;
      const states = siteBlock.getStates();
      if (states && states.size > 0) {
        blockState = IDUtils.getID(blockId, "states", 0);
      }

      const update = {
        blockId,
        blockState,
        elements: Immutable.Map(),
        canvasSetId: null,
        lastElement: null,
      };

      if (_.has(data, "layout")) {
        update.layout = data.layout;
      }

      // Reset selected and undo/redo on page change
      this.selected = this.selected.merge(update);
      UndoRedoManager.reset();

      this.trigger();
    }
  },

  onClearSelected() {
    this.selected = Immutable.fromJS(selectedModel);
    this.trigger();
  },

  onClearSelectedPage(layout) {
    const selectedPage = {
      ...selectedModel,
      layout,
    };
    this.selected = Immutable.fromJS(selectedPage);
    this.trigger();
  },

  onClearSelectedElements() {
    const elements = this.selected.get("elements");
    let editElem = false;

    elements.forEach((element) => {
      if (element.edit) {
        editElem = element;
      }
    });

    if (editElem) {
      editElem.edit = false;
      this.selected = this.selected.setIn(["elements", editElem.id], editElem);
    } else {
      this.selected = this.selected.set("elements", Immutable.Map());
      this.selected = this.selected.set("lastElement", null);
    }

    this.trigger();
  },

  onSelectBlock({ from, layout, id }) {
    const blockId = this.selected.get("blockId");

    let update = {
      from,
      layout,
    };

    if (blockId !== id) {
      update = this._getSelectedBlockDefaultSelectionData(id, update);
    }
    this.selected = this.selected.merge(update);
    this.trigger();
  },

  _getSelectedBlockDefaultSelectionData(blockId, /* Object */ update) {
    const block = this.sitePage.getBlock(blockId);
    let blockState = null;
    let canvasSetId = null;

    if (block) {
      let idFunc = BlockUtils.getStateID;
      let baseBlock = block;
      if (BlockUtils.isUsingSiteBlock(block)) {
        const siteBlock = this.siteDesign.getBlock(block.get(BLOCK_PROP_PARENT_BLOCK_ID));
        if (siteBlock) {
          baseBlock = siteBlock;
          idFunc = BlockUtils.getSiteBlockStateID;
        }
      }

      const states = baseBlock.get("states");
      // This code checks our data for any canvas views,
      // and if there are any, auto-selects the first canvas view to be opened (index 0)
      if (states && states.size > 0) {
        blockState = idFunc(blockId, 0);
      }
    } else {
      const canvasSet = this.isPartOfCanvasSet(blockId);
      if (canvasSet) {
        canvasSetId = canvasSet.get("id");
      }

      // Site blocks (e.g. canvas set blocks) aren't in sitePage, so check
      // siteDesign for canvas views that should be auto-selected
      const siteBlock = this.siteDesign.getBlockEntity(blockId);
      if (siteBlock) {
        const states = siteBlock.getStates();
        if (states && states.size > 0) {
          blockState = IDUtils.getID(blockId, "states", 0);
        }
      }
    }

    update.blockId = blockId;
    update.blockState = blockState;
    update.elements = {};
    update.canvasSetId = canvasSetId;
    update.lastElement = null;

    return update;
  },

  onSelectBlockState(data) {
    const update = {
      blockId: data.blockId,
      blockState: data.state,
      layout: data.layout || this.selected.get("layout"),
      from: data.from,
    };

    let elements = this.selected.get("elements");
    if (data.state == null) {
      elements = elements.filter((elem) => elem.stateId === -1);
    } else {
      const { blockState } = update;
      if (blockState) {
        const stateId = _.last(IDUtils.getArray(blockState));
        if (update.from === "panel") {
          elements = elements.filter((elem) => elem.stateId === stateId || elem.stateId === -1);
        } else {
          elements = elements.filter((elem) => elem.stateId !== stateId);
        }
      }
    }

    update.elements = elements;
    update.lastElement = null;

    this.selected = this.selected.merge(update);

    this.trigger();
  },

  onSelectCanvasSet({ canvasSet, layout = false }) {
    const canvasSetId = canvasSet.get("id");

    if (canvasSet) {
      this._resetSelection();

      this.blockContainerType = SITE_ITEM_SITE;

      const update = {
        blockId: null,
        canvasSetId,
        blockState: null,
        elements: Immutable.Map(),
        lastElement: null,
      };

      if (layout) {
        update.layout = layout;
      }

      // Reset selected and undo/redo on page change
      this.selected = this.selected.merge(update);
      UndoRedoManager.reset();

      this.trigger();
    }
  },

  /**
   * Clears selection in cross group child selection and enforces explicit in our out container selections.
   * @param {Object} props
   * @returns {boolean}
   */
  _clearCrossSelection({ elementId, elementIds, elements }) {
    const focusedGroupPath = GroupStore.getGroupSelectionId();

    // plus existing keys
    const selection = (elementId ? [elementId] : elementIds ? elementIds : []).concat([
      ...elements.keys(),
    ]);

    const selectionContainers = selection.reduce((result, id) => {
      const elementContainerPath = IDUtils.getElementContainerFromPath(IDUtils.getArray(id))?.join(
        "."
      );

      result.push(elementContainerPath);
      return result;
    }, []);

    return focusedGroupPath
      ? // maintain if every element to add is in same group
        !selectionContainers.every((selectionContainer) => selectionContainer === focusedGroupPath)
      : // clear if there's a selection container when one is not focused
        selectionContainers.filter((containerId) => !!containerId).length;
  },

  /**
   * Manages a new element(s) selection or deselection.
   * @todo refactor this at some point
   * @param {Object} data
   * @param {boolean} data.addRange - Whether to add to the current selection.
   * @param {boolean} data.addTo - Whether to add to the current selection.
   * @param {string} data.blockId - The ID of the selected block.
   * @param {string} data.from - The source of the selection, e.g., "stage".
   * @param {string} data.id - The IDs of the selected elements.
   * @param {Array} data.ids - The IDs of the selected elements.
   * @param {boolean} data.layout - Indicates if the layout is selected.
   */
  onSelectElement(data) {
    // Prevent race condition selections during duplication, especially from panel clicks
    if (this.isDuplicating && data.from === "panel") {
      return;
    }

    // filter removes undefined key/values. No idea where that's coming from.
    let elements = this.selected.get("elements");
    const selectedLayout = this.selected.get("layout");
    const blockId = this.selected.get("blockId");
    let lastElement = this.selected.get("lastElement", null);

    const {
      addTo = false,
      addRange = false,
      id: elementId,
      ids: elementIds = false,
      layout: eventLayout,
    } = data;

    const blockContainer = this.getBlockContainer();
    const block = blockContainer.getBlockEntity(blockId);

    const isSelectedElement = elements.has(elementId);

    // clear on cross group or in out additions
    if (
      ((addTo && !isSelectedElement) || addRange) &&
      this._clearCrossSelection({ elementId, elementIds, elements })
    ) {
      elements = elements.clear();
    }

    if (selectedLayout !== eventLayout || (!addTo && !addRange)) {
      elements = elements.clear();
      lastElement = null;
    }

    if (addRange && lastElement != null) {
      const clickedElementPath = IDUtils.getArray(elementId);
      const lastElementPath = IDUtils.getArray(lastElement);

      const elementPaths = [];
      elements.forEach((_, elemId) => {
        elementPaths.push(IDUtils.getArray(elemId));
      });

      const activeStateId = this.selected.get("blockState", null);

      let statePath = null;
      if (activeStateId) {
        statePath = IDUtils.getArray(activeStateId);
      }

      if (block) {
        const updatedSelection = block.determineSelectedElementsInRange(
          elementPaths,
          clickedElementPath,
          lastElementPath,
          statePath
        );

        updatedSelection.map((path) => {
          const elementData = this._getSelectedElementData(block, path);
          if (elementData) {
            elements = elements.set(elementData.id, elementData);
          }
        });
      }
    } else if (addTo && isSelectedElement) {
      elements = elements.delete(elementId);
      lastElement = null;
    } else if (elementIds) {
      // is this for a drag select or something?
      if (block) {
        elementIds.forEach((elementId) => {
          const elementData = this._getSelectedElementData(block, elementId);
          if (elementData) {
            elements = elements.set(elementData.id, elementData);
          }
        });
      }
    } else {
      if (block) {
        // elementData is null it means the element couldn't be found
        const elementData = this._getSelectedElementData(block, elementId);
        if (elementData) {
          elements = elements.set(elementData.id, elementData);
          lastElement = elementId;
        }
      }
    }

    this.selected = this.selected.merge({
      elements:
        elements.size > 0
          ? elements
          : elements.last()?.id
          ? Immutable.Map({ [elements.last()?.id]: elements.last() })
          : fromJS({}),
      layout: data.layout,
      from: data.from,
      lastElement,
    });

    UIActions.resetElementPropertiesPanel(data.type);

    this.trigger();
  },

  getButtonText({ id }) {
    const blockId = this.selected.get("blockId");
    const blockContainer = this.getBlockContainer();
    const block = blockContainer.getBlockEntity(blockId);
    const elements = block.data.get("elements");
    const buttonElKey = id.split(".");
    const buttonElIndex = buttonElKey[buttonElKey.length - 1];
    const buttonTextId = elements.getIn([buttonElIndex, "children", 0]);
    const buttonText = elements.find((el) => el.get("id") === buttonTextId);
    return buttonText;
  },

  /**
   * @param {object} data
   * @returns {void}
   * @description Returns the button text element key i.e. 'blockId123.elements.1'
   */
  onEditTextElement(data) {
    let elements = this.selected.get("elements");
    const element = elements.get(data.id);

    if (element) {
      element.edit = true;
      element.editor = _.get(data, "altEdit", false) ? ELEMENT_TYPE_HTML_TEXT : ELEMENT_TYPE_TEXT;
      elements = elements.set(data.id, element);

      this.selected = this.selected.merge({
        elements,
      });

      this.trigger();
    }
  },

  onSelectLayout(data) {
    this.selected = this.selected.merge({
      layout: data.layout,
      from: data.from,
    });

    this.trigger();
  },

  onLoadPage(data) {
    this.loadingPage = true;
  },

  /**
   * Used when API retrieves page data from remote source
   */
  onLoadPageComplete(result) {
    const isCorrectDesignVersion = isCorrectVersion(result?.data?.version);
    if (!isCorrectDesignVersion) {
      return this.onLoadPageFail("Page version mismatch");
    }
    this.loadingPage = false;
    this.loadingPageInfo = null;
    this.initPage = true;

    const page = result.data;
    const { eTag } = result;

    this._loadPage({ page, eTag });

    if (this._isInitComplete()) {
      if (this.initialPageLoad) {
        UIActions.togglePages({ activePanel: PANEL_LAYERS });
        UIActions.replaceActiveItemHistory({ id: page.id, type: SITE_ITEM_PAGE, name: page.name });
        this.initialPageLoad = false;
      }

      this.trigger();
    }
  },

  _loadPage(data) {
    const page = _.get(data, "page", false);
    const isNew = _.get(data, "isNew", false);
    let id = _.get(data, "id", false);
    const eTag = _.get(data, "eTag", null);

    // There is a current page loaded.
    if (!this.sitePage.data.isEmpty()) {
      DesignManager.setFileInUse(this.sitePage.id, false); // allow file to removed from manager after it syncs
    }

    // if an id is passed in then it means there should be an existing page being managed
    if (id === false) {
      id = page.id;
    }

    // check to see if this file is already being managed
    let pageFile = DesignManager.getFile(id);
    if (pageFile) {
      // if file is currently being managed use the data here instead of what was fetched.

      pageFile.inUse = true;

      this.sitePage = new SitePage(pageFile.data);
      this.sitePage.validate();
    } else {
      const [cleanedPage, dirty] = DesignPreparer.cleanBlockContainer(page);

      this.sitePage = new SitePage(Immutable.fromJS(cleanedPage));
      this.sitePage.validate();

      pageFile = new DesignFile({
        data: this.sitePage.data,
        siteId: this.id,
        saveFunc: API.saveDesignFile,
        eTag,
        isNew,
      });

      DesignManager.addFile(pageFile);

      // Track new pages and clean up site json if save fails
      if (isNew) {
        PageCleanupUtils.setupPageCleanupTracking({
          pageId: id,
          pageFile,
          pagesPendingSave: this.pagesPendingSave,
          store: this,
        });

        // Clean up orphaned page reference on save failure
        let cleanupSuccess = false;
        pageFile.once("error", (error) => {
          cleanupSuccess = PageCleanupUtils.handlePageSaveFailure({
            pageId: id,
            pageFile,
            pagesPendingSave: this.pagesPendingSave,
            siteDesign: this.siteDesign,
            currentPage: this.sitePage,
            onSelectPage: this.onSelectPage.bind(this),
          });
        });

        if (cleanupSuccess) {
          this._updateDesignFiles();
          DesignManager.saveNow();
          this._updateLists();
          this.trigger();
        }
      }

      if (!AppConfig.demoMode && (isNew || dirty)) {
        pageFile.inSync = false;
        DesignManager.saveNow();
      }
      SiteActions.pageLoaded();
    }

    const currentLayout = this.selected.get("layout");

    /* Reset selected and undo/redo on page change */
    this._resetSelection();
    UndoRedoManager.reset();

    this.selected = this.selected.set("layout", currentLayout);

    AppActions.storeValue(SITE_INIT_PAGE_KEY, id);
  },

  onLoadPageFail(err) {
    console.error("Page Load", err);
    // Failures can occur without a page in flight, when loadingPageInfo is null.
    const pageInfo = this.loadingPageInfo;
    if (pageInfo) {
      // eslint-disable-next-line no-alert
      alert(`Can't Load Page: ${pageInfo.get("name")} [${pageInfo.get("id")}]`);
    }
    this.loadingPage = false;
    this.loadingPageInfo = null;
  },

  onAddDesignItem(data) {
    if (_.isEmpty(data, "item")) {
      return;
    }

    const [result] = DesignPreparer.prepareDesignItem(data, this.siteDesign);
    const defaultCanvasSet = this.siteDesign.getDefaultCanvasSet();

    if (!_.isEmpty(result, "item")) {
      if (!_.isEmpty(result, "theme")) {
        this.siteDesign.updateTheme(result.theme);
      }

      const pageConfig = {
        canvasSet: defaultCanvasSet ? fromJS([defaultCanvasSet.get("id")]) : List(),
      };

      const type = _.get(result, "type");
      switch (type) {
        case DESIGN_TYPE_BLOCK: // only put into page
          this._addBlockToContainer({ block: result.item }, this.sitePage);
          break;
        case DESIGN_TYPE_SITE_BLOCK: // only put into site
          this._addBlockToContainer({ block: result.item }, this.siteDesign);
          this._updateSiteBlockList();
          break;
        case DESIGN_TYPE_PAGE: // only put into site
          this._addNewPage(result.item, pageConfig);
          break;
        case DESIGN_TYPE_WP_PAGE: // only put into site
          this._addNewPage(result.item, pageConfig);
          break;
        default:
      }
    }

    this.trigger();
  },

  onCreateCanvasSet() {
    const { siteDesign } = this;
    const canvasSet = siteDesign.createCanvasSet();
    this._updateDesignFiles();
    this._updateLists();
    this.trigger();
    this.onSelectCanvasSet({ canvasSet });
  },

  onAddPage(data) {
    const { siteDesign } = this;
    const config = _.pick(data, ["toWP"]);
    const defaultCanvasSet = this.siteDesign.getDefaultCanvasSet();
    if (defaultCanvasSet) {
      config.canvasSet = List([defaultCanvasSet.get("id")]);
    }

    const newPage = DesignPreparer.preparePage(null, config, siteDesign);

    this._addNewPage(newPage, config);
    this.blockContainerType = SITE_ITEM_PAGE;

    this.trigger();
  },

  /**
   *
   * @param {Object} data
   * @param {boolean=} data.isWp
   */
  onAddFolder(data = {}) {
    const { siteDesign } = this;

    UndoRedoManager.save();

    const config = _.pick(data, ["isWP"]);

    const baseFolder = {
      name: "New Folder",
      slug: "new-folder",
    };

    const { id, ...rest } = siteDesign.addPageFolder({ ...baseFolder, ...data }, config);

    this._updateDesignFiles();
    this._updateLists();
    this.trigger();

    UIActions.toggleRenamePopup({
      title: ITEM_TYPE_PAGE_FOLDER,
      itemType: ITEM_TYPE_PAGE_FOLDER,
      itemId: id,
      ...rest,
    });
  },

  /**
   *
   * @param {object} newPage
   */
  _addNewPage(newPage, config = {}) {
    const page = this.siteDesign.addNewPage(newPage, config);

    this._updateDesignFiles();

    this._loadPage({ page, isNew: true });

    this._updateDesignFiles();

    this._updateLists();
  },

  onTogglePageIsPublishable(pageId) {
    this.getDesign().toggleIsPagePublishable(pageId);

    this._updateDesignFiles();
    this._updatePageList();
    this.trigger();
  },

  onRemoveFolder(data) {
    const { id } = data;

    if (!id) return;

    UndoRedoManager.save();

    this.siteDesign.removePageFolder(id);

    this._updateDesignFiles();
    this._updateLists();
    this.trigger();
  },

  onRemovePage(data) {
    const { id } = data;

    const result = this.siteDesign.removeItem(id);

    if (result.index !== -1) {
      // if the page being removed is the current page then select the previous page
      if (this.sitePage.data.get("id") === id) {
        // select the previous page to select
        const selectPageIndex = Math.max(0, result.index - 1);
        const pageItem = this.siteDesign.getPageItemByIndex(selectPageIndex);
        if (pageItem) {
          this.onSelectPage({ page: pageItem });
        }
      }

      this._updateDesignFiles();
      this._updateLists();
      this.trigger();
    }
  },

  onRemoveCanvasSet({ id }) {
    const { siteDesign } = this;
    const deletedCanvasIndex = siteDesign.removeCanvasSet({ id });

    if (deletedCanvasIndex === -1) return;

    const canvasSetList = this.siteDesign.getCanvasSetsList();

    if (canvasSetList.size) {
      const nextCanvasIndex = Math.max(0, deletedCanvasIndex - 1);
      const nextCanvasId = canvasSetList.get(nextCanvasIndex);
      const nextCanvas = this.siteDesign.getCanvasSet({ id: nextCanvasId });

      this.onSelectCanvasSet({ canvasSet: nextCanvas });
    } else {
      const homePage = this.siteDesign.getHomePage();
      if (homePage) {
        this.onSelectPage({ page: homePage });
      } else {
        // TODO: figure out what to do here?
      }
    }

    // this
    this._updateDesignFiles();
    this._updateLists();
    this.trigger();
  },

  onMakeCanvasSetDefault({ id }) {
    const { siteDesign } = this;
    siteDesign.setAsDefaultCanvasSet(id);
    this._updateDesignFiles();
    this._updateLists();
    this.trigger();
  },

  /**
   * @typedef pageDuplicationProps
   * @prop {string} id - id of the page to duplicate
   * @prop {string=} toPage
   * @prop {string=}  toWP
   */

  /**
   * Duplicate a page
   * @param {pageDuplicationProps} data
   */
  onDuplicatePage(data) {
    const { siteDesign } = this;
    const config = _.pick(data, ["toWP", "toPage"]);
    const designId = this.id;
    const pageId = data.id;
    const canvasSet = siteDesign.getPageCanvasSetList({ id: pageId });
    const basePageWp = !!siteDesign.getPageItem(pageId)?.get("wp");
    // remove previous pId if changing blog status
    const pId = config.toWP === basePageWp ? siteDesign.getPageItem(pageId)?.get("pId") : undefined;

    config.insertAt = this.siteDesign.getPageIndex(pageId) + 1;

    // only load page if it's not the current page
    const pageData =
      this.sitePage.id === pageId
        ? Promise.resolve({ data: this.sitePage.data.toJS() })
        : SiteService.loadPage({ pageId, designId });

    pageData.then(({ data: newPage }) => {
      const preparedPage = DesignPreparer.preparePage(newPage, config, siteDesign);
      const addedPage = siteDesign.addNewPage(preparedPage, { ...config, canvasSet, pId });

      this._updateDesignFiles();
      this._loadPage({ page: addedPage, isNew: true });
      this._updateDesignFiles();
      this._updateLists();
      this.blockContainerType = SITE_ITEM_PAGE;
      this.trigger();
    });
  },

  onSetAsHomePage(data) {
    const { id } = data;

    UndoRedoManager.save();

    this.siteDesign.setAsHomePage(id);

    this._updateDesignFiles();

    this._updateLists();
    this.trigger();
  },

  onUpdateItemName(data) {
    const type = _.get(data, "type", false);
    const id = _.get(data, "id", false);
    const name = _.get(data, "name");

    // todo: rename passed-through id to just "id", idk why they are differently named if all are local to this file
    if (type && id) {
      const updateActions = {
        [ITEM_TYPE_BLOCK]: () => this.onUpdateBlockName({ blockId: id, name }),
        [ITEM_TYPE_ELEMENT]: () => this.onUpdateElementName({ id, name }),
        [ITEM_TYPE_PAGE_FOLDER]: () => this.onUpdateFolderName({ id, name }),
        [ITEM_TYPE_PAGE]: () => this.onUpdatePageName({ id, name }),
        [ITEM_TYPE_STATE]: () => this.onUpdateBlockStateName({ stateId: id, name }),
        [ITEM_TYPE_SITE_BLOCK]: () => this.onUpdateBlockName({ blockId: id, name }),
        [ITEM_TYPE_CANVAS_SET]: () => this.onUpdateCanvasSetName({ id, name }),
      };

      const updateAction = updateActions[type];
      updateAction && updateAction();
    }
  },

  onUpdateElementName(data) {
    const id = _.get(data, "id", false);
    const name = _.get(data, "name");

    if (id) {
      const blockContainer = this.getBlockContainer();
      const path = IDUtils.getArray(id);
      const block = blockContainer.getBlockEntityByPath(path);

      if (block) {
        UndoRedoManager.save();

        block.updateElementName(path, name);
        block.applyToParent();

        this._updateDesignFiles();
        this.trigger();
      }
    }
  },

  onCreateElements(assets) {
    if (!_.isEmpty(assets)) {
      const elements = [];
      _.forEach(assets, (asset) => {
        const element = ModelFactory.createElementFromAsset(asset);
        if (element) {
          elements.push(element);
        }
      });

      this.onAddElement({ elements, isNew: true });
    }
  },

  onCreateElement(data) {
    const { asset } = data;
    const element = ModelFactory.createElementFromAsset(asset);

    // No support element type found for asset, just return and don't add to design
    if (_.isEmpty(element)) {
      return;
    }

    this.onAddElement({ element, isNew: true });
  },

  /**
   * Adds an element
   * @param {Object} data
   * @param {boolean=} data.addUndo
   * @param {string=} data.blockId - target block id; overrides the currently-selected block.
   * @param {string=} data.containedBy - element selection id to contain added element.
   * @param {Object} data.element
   * @param {[Object]} data.elements
   * @param {number=} data.insertAt
   * @param {boolean=} data.insertOutsideSelectedState
   * @param {boolean} data.isNew
   * @param {Object} data.select
   * @param {boolean=} data.maintainPosition
   * @param {boolean=} data.prepare
   */
  onAddElement({
    addUndo = true,
    blockId: explicitBlockId,
    containedBy,
    element,
    elements: elementsList,
    insertOutsideSelectedState,
    isNew,
    insertAt,
    prepare = true,
    select = true,
    maintainPosition = false,
  }) {
    let elements = elementsList ? elementsList : element ? [element] : [];

    const blockContainer = this.getBlockContainer();

    const blockId = explicitBlockId ?? this.getAddElementBlockId();
    const stateId =
      explicitBlockId && explicitBlockId !== this.selected.get("blockId")
        ? null
        : this.selected.get("blockState");

    let stateIndex = null;
    let block = null;

    if (stateId && !insertOutsideSelectedState) {
      stateIndex = IDUtils.getArray(stateId).at(-1);
    }

    /** @type {Block} */
    block = blockContainer.getBlockEntity(blockId);

    if (block) {
      if (isNew && prepare) {
        // use container element for dimensions if contained by
        const container = containedBy ? { data: this.getElementData(containedBy, blockId) } : block;

        elements = elements.map((elem) =>
          DesignPreparer.prepareElement(elem, this.siteDesign, container, maintainPosition)
        );
      }

      const config = { insertAt, containedBy, isNew };

      const newElementPaths = block.addElements(elements, stateIndex, config);

      // cycle ids if an added element is an elementContainer
      newElementPaths.forEach((path) => {
        const elementData = block.getElement(path);
        const type = elementData.get("type");

        if (type === DataTypes.GROUP) {
          block.getElementContainerEntity(path)?.cycleIds();
        }
      });

      // remove group if resulting addition to element container results in less than two elements
      if (containedBy) {
        const container = block.getElementContainerEntity(containedBy);

        if (container.shouldDissolve()) {
          block.applyToParent();
          return this.dissolveGroup({ id: containedBy }, block.id, addUndo);
        }

        // resize if container isn't being dissolved and element isn't new (new cannot resize)
        if (!isNew) {
          const layout = this.selected.get("layout");
          GroupActions.updateGroupData(container.getLayoutPosition(layout), layout);
        }
      }

      if (addUndo) UndoRedoManager.save();

      block.applyToParent();

      this._updateDesignFiles();

      if (select) {
        const selectionPaths = newElementPaths.filter((path) => block.getElement(path));
        this._selectNewElements(selectionPaths, block);
      }

      this.trigger();
    }
  },

  /**
   * Selects newly created elements.
   * @param {Array<Array<string> | string>} elementPaths
   * @param {Block} block
   */
  _selectNewElements(elementPaths, block) {
    let selectedElements = this.selected.get(ELEMENTS);
    selectedElements = selectedElements.clear();
    selectedElements = elementPaths.reduce((result, path) => {
      const data = this._getSelectedElementData(block, path);
      return data ? result.set(data.id, data) : result;
    }, selectedElements);
    this.selected = this.selected.merge({ elements: selectedElements });
  },

  /**
   * Duplicates an element in the design
   * @param {string} elementId - ID of the element to duplicate
   * @param {Object} [options] - Optional configuration object
   * @param {boolean} [options.skipSave] - Whether to skip saving the undo/redo state
   */
  onDuplicateElement(elementId, options = {}) {
    const elementPath = IDUtils.getArray(elementId);
    const blockId = elementPath[0];

    const blockContainer = this.getBlockContainer();
    const block = blockContainer.getBlockEntity(blockId);

    if (block) {
      // Set duplication flag to prevent race condition selections
      this.isDuplicating = true;

      if (!options.skipSave) {
        UndoRedoManager.save();
      }

      const newElementPath = block.duplicateElementAt(elementPath, options);
      const newElement = block.data.getIn(IDUtils.getArray(newElementPath, 1));
      const isGroup = newElement.get("type") === DataTypes.GROUP;
      const containerPath = isGroup
        ? newElementPath
        : IDUtils.getElementContainerFromPath(IDUtils.getArray(elementPath));

      if (containerPath && isGroup) {
        const elementContainer = block.getElementContainerEntity(containerPath);
        elementContainer && elementContainer.cycleIds();
      }

      block.applyToParent();

      this._selectNewElements([newElementPath], block);

      this._updateDesignFiles();
      this.trigger();

      // Clear duplication flag after a short delay to allow any pending events to be ignored
      // todo: figure out whats causing the double selection
      setTimeout(() => {
        this.isDuplicating = false;
      }, 100);
    }
  },

  /**
   * Remove single element. Used by layers panel.
   */
  onRemoveElement(elementId, skipSave = false) {
    const [blockId] = elementId.split(".");

    const blockContainer = this.getBlockContainer();
    const block = blockContainer.getBlockEntity(blockId);
    if (!block) return;

    if (!skipSave) {
      UndoRedoManager.save();
    }

    this.selected = this.selected.set("elements", this.selected.get("elements").clear());

    const elementPath = IDUtils.getArray(elementId);

    block.removeElements([elementPath]);

    block.applyToParent();

    this._updateDesignFiles();
    this.trigger();
  },

  /**
   * Remove elements in selection.
   */
  onRemoveSelectedElements(skipDissolve) {
    const blockId = this.selected.get("blockId");
    const selectedElements = SelectionStore.getFullElementsJS();

    if (
      blockId != null &&
      selectedElements.length &&
      !(selectedElements.length === 1 && selectedElements[0]?.lockEditing === "1")
    ) {
      const blockContainer = this.getBlockContainer();
      /** @type {Block} */
      const block = blockContainer.getBlockEntity(blockId);

      if (block) {
        const elementPaths = selectedElements.reduce((result, element) => {
          element.lock !== "1" && result.push(element.selected.id.split("."));
          return result;
        }, []);

        UndoRedoManager.save();

        this.selected = this.selected.set("elements", this.selected.get("elements").clear());

        block.removeElements(elementPaths);

        // check if contained and should dissolve
        const elementContainerPath = IDUtils.getElementContainerFromPath(elementPaths[0]);
        let elementContainer =
          elementContainerPath && block.getElementContainerEntity(elementContainerPath);

        if (!skipDissolve && elementContainer && elementContainer.shouldDissolve()) {
          block.applyToParent();
          return this.dissolveGroup({ id: elementContainerPath.join(".") }, blockId, false);
        }

        block.applyToParent();

        this._updateDesignFiles();
        this.trigger();
      }
    }
  },

  onRestorePageOld(page) {
    this.siteDesign.addPage(page);

    const obj = this.siteDesign.data.toJS();
    SiteActions.saveJSON({ object: obj });
    AdminActions.restorePageCompleted();
  },

  onRestorePageCurrent(page) {
    const eTag = _.get(page, "eTag", null);
    const payload = {
      page: Immutable.fromJS(page),
      id: false,
      isNew: true,
      eTag,
    };
    this._loadPage(payload);

    const obj = this.sitePage.data.toJS();
    SiteActions.saveJSON({ object: obj });
    AdminActions.restorePageCompleted();
  },

  /**
   * Handles the duplication of a site canvas as a page canvas
   * @param {string} blockId - The ID of the site canvas to duplicate
   * @returns {boolean} - True if the duplication was successful, false otherwise
   */
  _handleSiteCanvasCanvasDuplication(blockId) {
    const pageBlock = this.sitePage.getBlock(blockId);
    const siteBlockId = pageBlock.get(AppConstants.BLOCK_PROP_PARENT_BLOCK_ID);
    const siteBlock = this.siteDesign.getBlock(siteBlockId);

    if (!pageBlock || !siteBlock) {
      return false;
    }

    const newPageCanvas = BlockDuplicationUtils.duplicateSiteCanvasAsPageCanvas(
      pageBlock,
      siteBlock
    );
    const blockContainer = this.getBlockContainer();
    const blockIndex = blockContainer.getBlockIndex(blockId);
    this._addBlockToContainer({ block: newPageCanvas, insertAt: blockIndex + 1 }, blockContainer);

    return true;
  },

  onDuplicateBlock(data) {
    const { id: blockId, siteCanvas } = data;

    if (blockId) {
      if (this.blockContainerType === SITE_ITEM_PAGE && this._handleSiteCanvasCanvasDuplication(blockId)) {
        this._updateDesignFiles();
        this.trigger();
        return;
      }
      
      if (siteCanvas && this.blockContainerType !== SITE_ITEM_PAGE) {
        // switch to site block container if block is a site canvas
        this.blockContainerType = SITE_ITEM_SITE;
      }
      const blockContainer = this.getBlockContainer();
      const block = blockContainer.getBlock(blockId);
      const blockIndex = blockContainer.getBlockIndex(blockId);
      if (block) {
        const blockObj = block.toJS();
  
        this._addBlockToContainer({ block: blockObj, insertAt: blockIndex + 1 }, blockContainer);
  
        if (SITE_ITEM_SITE === blockContainer.type) {
          this._updateSiteBlockList();
        }
  
        this.trigger();
      }
    }
  },

  onDuplicateCanvasSet({ id }) {
    const canvasSet = this.siteDesign.duplicateCanvasSet(id);
    this._updateDesignFiles();
    this._updateLists();
    this.onSelectCanvasSet({ canvasSet });
    this.trigger();
  },

  /**
   * Takes the block data provided and copies everything into the block specified by the id.
   * Only the name and id remain the same for the block that receives the data.
   * @param data
   * @param data.block - A javascript object that represents the block to be copied from
   * @param data.id    - The id of the block that will receive the data.
   */
  onCopyBlockIntoBlock(data) {
    const blockId = _.get(data, "id", false);
    let copiedBlock = _.get(data, "block", false);

    if (blockId && copiedBlock) {
      const blockContainer = this.getBlockContainer();
      const block = blockContainer.getBlock(blockId);

      if (block) {
        UndoRedoManager.save();

        // strip out wordpress properties if container does not allow them
        if (!blockContainer.isWordpressAllowed()) {
          copiedBlock = _.cloneDeep(copiedBlock);
          copiedBlock = WordpressRemover.removeFromBlock(copiedBlock);
        }

        blockContainer.overwriteBlock(blockId, copiedBlock);

        // reset select elements in block that is being copied into
        this.selected = this.selected.set("elements", Immutable.Map());
        this.selected = this.selected.set("blockState", null);

        this._updateDesignFiles();
        this.trigger();
      }
    }
  },

  onConvertToSiteBlock(data) {
    const blockId = _.get(data, "id");

    UndoRedoManager.save();

    this.siteDesign.convertToSiteBlock(blockId, this.sitePage);

    this._updateDesignFiles();
    this._updateSiteBlockList();

    // reset selection for block since type is changing
    const update = this._getSelectedBlockDefaultSelectionData(blockId, {});
    this.selected = this.selected.merge(update);

    this.trigger();
  },

  onConvertToPageBlock(data) {
    const blockId = _.get(data, "id");

    UndoRedoManager.save();

    this.sitePage.convertToPageBlock(blockId, this.siteDesign);

    this._updateDesignFiles();

    // reset selection for block since type is changing
    const update = this._getSelectedBlockDefaultSelectionData(blockId, {});
    this.selected = this.selected.merge(update);

    this.trigger();
  },

  /**
   *
   * @param {{
   *  id: string,
   *  blockId: string,
   *  index: number
   *  area: "top" | "bottom"
   * }} data
   */
  onAddCanvasToCanvasSet(data) {
    UndoRedoManager.save();
    this.siteDesign.insertCanvasIntoSet({ ...data });
    this._updateDesignFiles();
    this._updateLists();

    this.trigger();
  },

  onRemoveCanvasFromCanvasSet(data) {
    UndoRedoManager.save();
    this.siteDesign.removeCanvasFromSet({ ...data });
    this._updateDesignFiles();
    this._updateLists();

    this.trigger();
  },

  /**
   * Adds a new site block.
   * @param data should have a block property that is a Javascript Object not an Immutable Object
   */
  onAddSiteBlock(data) {
    this._addBlockToContainer(data, this.siteDesign);
    this._updateSiteBlockList();
    this.blockContainerType = SITE_ITEM_SITE;
    this.trigger();

    SiteActions.addSiteBlockCompleted();
  },

  /**
   * Adds a new block.
   * @param data should have a block property that is a Javascript Object not an Immutable Object
   */
  onAddBlock(data) {
    if (_.has(data, "block") && _.has(data, "applyTheme")) {
      const { theme } = this.siteDesign;
      const { block } = data;
      data.block = applyThemeBG(block, theme);
    }

    this._addBlockToContainer(data, this.sitePage);
    this.trigger();

    SiteActions.addBlockCompleted();
  },

  /**
   * Gets all blocks in a canvas set
   * @param {string} canvasSetId
   * @returns {Immutable.Map} - A map of block IDs to block objects for all valid, non-empty blocks in the canvas set
   */
  _getCanvasSetBlocks(canvasSetId) {
    const canvasSet = this.siteDesign.getCanvasSet({ id: canvasSetId });

    const canvases = canvasSet?.get(CANVAS_SET_CHILDREN) ?? Immutable.List();

    const result = canvases.reduce((blockAcc, child) => {
      const block = this.siteDesign.getBlock(child);
      if (block === CANVAS_SET_BLOCK_CONTENT_SEPARATOR) {
        return blockAcc;
      }

      if (block && !block.isEmpty()) {
        blockAcc = blockAcc.set(block.get("id"), block);
      }
      return blockAcc;
    }, Immutable.Map());

    return result;
  },

  /**
   * Gets all blocks on the page (including blocks in canvas sets)
   * @returns {Immutable.Map} - A map of block IDs to block objects for all blocks on the page and in canvas sets
   */
  _getAllRelevantBlocks(blockContainer) {
    const pageBlocks = blockContainer.getBlocks();

    const pageCanvasSetIds = this.getCurrentPage()
      ? this.siteDesign.getPageCanvasSetList({ id: this.getCurrentPage().id }).toArray()
      : [];

    const selectedCanvasSetId = this.selected.get("canvasSetId");

    const additionalCanvasSetIds = selectedCanvasSetId ? [selectedCanvasSetId] : [];

    const allCanvasSetIds = [...pageCanvasSetIds, ...additionalCanvasSetIds];

    const canvasSetBlocks = allCanvasSetIds.reduce((acc, canvasSetId) => {
      const blocks = this._getCanvasSetBlocks(canvasSetId);
      return acc.merge(blocks);
    }, Immutable.Map());

    const finalBlocks = pageBlocks.merge(canvasSetBlocks);

    return finalBlocks;
  },

  /**
   * Adds block data to the provided block container
   * @param {*} data
   * @param {BlockContainer} blockContainer
   */
  _addBlockToContainer(data, blockContainer) {
    let blockData = data.block;
    const layout = this.selected.get("layout");
    const config = _.pick(data, ["insertAt"]);

    UndoRedoManager.save();

    // strip out wordpress properties if container does not allow them
    if (!blockContainer.isWordpressAllowed()) {
      blockData = _.cloneDeep(blockData);
      blockData = WordpressRemover.removeFromBlock(blockData);
    }

    const allRelevantBlocks = this._getAllRelevantBlocks(blockContainer);
    const newBlock = blockContainer.addBlock(blockData, config, allRelevantBlocks);
    this.onSelectBlock({ id: newBlock.get("id"), layout });

    this._updateDesignFiles();
  },

  /**
   * Adds a new site block.
   * @param data should have a block property that is an Immutable Object
   */
  onRemoveSiteBlock(data) {
    const { block } = data;
    const id = block.get("id");
    const blockIndex = this.siteDesign.getBlockIndex(id);

    this._removeBlock(block, this.siteDesign);
    this._updateSiteBlockList();

    if (blockIndex >= 0) {
      const nextBlockIndex = this.siteDesign.getBlockIndex(Math.max(0, blockIndex - 1));
      const nextBlock = this.siteDesign.getBlockAtIndex(nextBlockIndex);
      if (nextBlock) {
        this.onSelectSiteBlock({ block: nextBlock });
      }
    }

    // remove siteBlock from canvasSets
    this.siteDesign.getCanvasSets().forEach((canvasSet) => {
      const canvasSetId = canvasSet.get("id");
      const canvasSetBlockList = canvasSet.get(CANVAS_SET_CHILDREN, List());
      if (canvasSetBlockList.indexOf(id) !== -1) {
        this.siteDesign.removeCanvasFromSet({ id: canvasSetId, blockId: id });
      }
    });

    this.trigger();
  },

  onRemoveBlock(data) {
    const { block } = data;

    this._removeBlock(block, this.sitePage);

    this.trigger();
  },

  /**
   * Removes block from container
   * @param {Map} blockImm
   * @param {BlockContainer} blockContainer
   */
  _removeBlock(blockImm, blockContainer) {
    UndoRedoManager.save();

    blockContainer.deleteBlock(blockImm.get("id"));

    this._updateDesignFiles();
  },

  onDuplicateBlockState(data) {
    const statePath = IDUtils.getArray(data.id);
    const blockId = statePath[0];
    const stateIndex = parseInt(statePath[2], 10);

    const blockContainer = this.getBlockContainer();
    const block = blockContainer.getBlockEntity(blockId);

    if (block) {
      const state = block.getState(stateIndex);

      if (state) {
        const stateObj = state.toJS();

        const dupData = {
          id: blockId,
          name: state.get("name"),
          insertAt: stateIndex + 1,
          blockState: stateObj,
        };

        this.onAddBlockState(dupData);
      }
    }
  },

  onAddBlockState(data) {
    const config = _.pick(data, ["insertAt"]);

    const blockContainer = this.getBlockContainer();
    const block = blockContainer.getBlockEntity(data.id);
    const layout = this.selected.get("layout");

    const blockStateData = data.blockState ? data.blockState : ModelFactory.getBlockState();

    if (_.has(data, "name")) {
      blockStateData.name = data.name;
    } else {
      _.unset(blockStateData, "name"); // remove default name so that it can be generated
    }

    UndoRedoManager.save();

    const { index } = block.addState(blockStateData, config);

    const stateId = BlockUtils.getStateID(block.id, index);
    const statePath = BlockUtils.trimIndexFromPath(stateId);

    // make sure to clear any marked elements and states before saving
    block.clearMarked();

    block.applyToParent();

    this._updateDesignFiles();

    this.trigger();

    // Now pre-select the newly added state and keep it open
    UIActions.selectBlockState({
      state: stateId,
      statePath,
      layout,
      blockId: block.id,
      from: "panel",
    });
  },

  onRemoveBlockState(data) {
    const stateId = data.id;
    const statePath = IDUtils.getArray(stateId);
    const blockId = statePath[0];
    const stateIndex = statePath[2];

    const blockContainer = this.getBlockContainer();

    if (blockContainer.hasBlock(blockId)) {
      UndoRedoManager.save();

      // need to record currently selected items because removing a state may cause index of elements to change
      // make sure to mark selected before saving and getting block
      this._markSelected(this.selected, blockContainer);

      const block = blockContainer.getBlockEntity(blockId);

      block.removeState(stateIndex);

      // apply any changes to parent container
      block.applyToParent();

      this.selected = this._refreshSelected(this.selected, blockContainer);

      // make sure to clear any marked elements and states before saving
      block.clearMarked();

      block.applyToParent();

      this._updateDesignFiles();
      this.trigger();
    }
  },

  onUpdateBlockStateName(data) {
    const { stateId } = data;
    const { name } = data;

    const path = IDUtils.getArray(stateId);
    if (path.length !== 3) {
      return;
    }

    const blockId = path[0];
    const stateIndex = path[2];

    const blockContainer = this.getBlockContainer();
    const block = blockContainer.getBlockEntity(blockId);

    UndoRedoManager.save();

    block.updateStateName(stateIndex, name);

    block.applyToParent();

    this._updateDesignFiles();

    this.trigger();
  },

  onUpdateBlockName(data) {
    const blockContainer = this.getBlockContainer();

    UndoRedoManager.save();

    const allRelevantBlocks = this._getAllRelevantBlocks(blockContainer);
    blockContainer.updateBlockName(data.blockId, data.name, allRelevantBlocks);

    this._updateDesignFiles();

    if (SITE_ITEM_SITE == blockContainer.type) {
      this._updateSiteBlockList();
    }

    this.trigger();
  },

  onUpdateCanvasSetName({ name, id }) {
    UndoRedoManager.save();

    this.siteDesign.updateCanvasSetName({ name, id });
    this._updateDesignFiles();
    this._updateLists();
    this.trigger();
  },

  /**
   *
   * @param {vo/CanvasSetsUpdate} canvasSetUpdates
   */
  onUpdateCanvasSet(canvasSetUpdates) {
    UndoRedoManager.save();

    this.siteDesign.applyUpdate(canvasSetUpdates);
    this._updateDesignFiles();
    this._updateLists();
    this.trigger();
  },

  onUpdatePage(pageUpdate /* vo/PageUpdate */) {
    const pageId = pageUpdate.id;

    if (pageId !== this.sitePage.id) {
      return;
    }

    if (
      pageUpdate.updates[0]?.source !== INPUT_SOURCE_PROPERTY_INPUT ||
      pageUpdate.updates[0]?.addUndo
    ) {
      UndoRedoManager.save();
    }

    this.sitePage.applyUpdate(pageUpdate);

    this._updateDesignFiles();
    this.trigger();
  },

  /**
   * Updates a folder's name
   * @param {{
   *  id: string,
   *  name: string
   * }} data
   */
  onUpdateFolderName(data) {
    UndoRedoManager.save();

    this.siteDesign.updateFolderName(data);

    this._updateDesignFiles();
    this._updatePageList();

    this.trigger();
  },

  onUpdatePageName(data) {
    UndoRedoManager.save();

    this.siteDesign.updatePageName(data.name, this.sitePage);

    this._updateDesignFiles();
    this._updatePageList();

    this.trigger();
  },

  onUpdatePageWpTemplate(data) {
    UndoRedoManager.save();

    this.siteDesign.updatePageWPTemplate(data, this.sitePage);

    const blocks = this.sitePage.getBlocks();
    blocks.forEach((block) => {
      if (
        block.getIn(["wp", "showOnFirst"], false) &&
        !PAGINATED_TEMPLATE_TYPES[this.sitePage.data.getIn(["wp", "template"])]
      ) {
        const update = new BlockUpdateVO();
        update.deleteProp(block.get("id"), ["wp", "showOnFirst"]);
        const blockEntity = this.sitePage.getBlockEntity(update.id);
        if (blockEntity) {
          blockEntity.applyUpdate(update);
          blockEntity.applyToParent();
        }
      }
    });

    this._updateDesignFiles();
    this._updatePageList();

    this.trigger();
  },

  onSaveJSON(data) {
    const obj = data.object;
    let objKey = null;

    switch (obj.type) {
      case SITE_ITEM_PAGE:
      case SITE_ITEM_WP_PAGE:
        objKey = "sitePage";
        break;
      case SITE_ITEM_SITE:
        objKey = "siteDesign";
        break;
      default:
    }

    if (objKey != null) {
      UndoRedoManager.save();

      this[objKey].data = Immutable.fromJS(obj);

      this._updateDesignFiles();
      this._updateLists();
      this.trigger();
    }
  },

  onUpdateBlock(/* vo/BockUpdate */ data) {
    const blockId = data.id;
    const blockContainer = this.getBlockContainer();
    const block = blockContainer.getBlockEntity(blockId);

    if (block) {
      if (data.updates[0]?.source !== INPUT_SOURCE_PROPERTY_INPUT || data.updates[0]?.addUndo) {
        UndoRedoManager.save();
      }

      block.applyUpdate(data);

      block.applyToParent();

      this._updateSelectionData(blockContainer);
      this._updateDesignFiles();
      this.trigger();
    }
  },

  onReplaceBlock(block) {
    UndoRedoManager.save();

    this.sitePage.overwriteBlock(block.id, block);

    this._updateDesignFiles();
    this._updateLists();
    this.trigger();

    SiteActions.replaceBlockCompleted();
  },

  _getResizeProps(ratio) {
    const wide = ratio > 1;
    return {
      side: wide ? "w" : "h",
      sign: wide ? -1 : 1,
      name: wide ? "h" : "w",
    };
  },

  /**
   * Resizes image element based on aspect ratio, but based on longest edge. Also resets mode to a center fill.
   * @param {{
   *  element: Object,
   *  id: string,
   *  ratio: number,
   *  layout: Layout,
   *  update: BlockUpdateVO
   * }} props
   */
  _resizeZoomedImage({ element, id, ratio, layout, update }) {
    const typeIsMerged = isPropSynced(element, GRAPHIC_STYLE_TYPE);
    const height = new Big(element.getIn([layout, "h"], 0));
    const width = new Big(element.getIn([layout, "w"], 0));

    const baseSide = height.lt(width) ? "h" : "w";

    const targetSide = baseSide === "w" ? "h" : "w";
    const targetValue = baseSide === "h" ? height.times(ratio) : width.div(ratio);

    update.addPropUpdate(id, {
      name: `${layout}.${targetSide}`,
      value: targetValue.round().toNumber(),
    });

    if (!(layout === LAYOUT_MOBILE && typeIsMerged)) {
      // fill resets
      update.add(
        id,
        layout,
        Immutable.Map()
          .setIn(GRAPHIC_STYLE_TYPE.split("."), "cover")
          .setIn(GRAPHIC_STYLE_HORIZONTAL_SHIFT.split("."), 50)
      );

      // remove crop props
      update.deleteProp(id, `${layout}.${GRAPHIC_STYLE_SCALE_PERCENTAGE}`);
      update.deleteProp(id, `${layout}.${GRAPHIC_STYLE_OFFSET_X}`);
      update.deleteProp(id, `${layout}.${GRAPHIC_STYLE_OFFSET_Y}`);
    }
  },

  _resizeElement(element, id, ratio) {
    const update = new BlockUpdateVO();
    const { side, sign, name } = this._getResizeProps(ratio);
    const elementType = element.get("type");

    [LAYOUT_DESKTOP, LAYOUT_MOBILE].forEach((layout) => {
      if (elementType === GRAPHIC && getProp(element, layout, GRAPHIC_STYLE_TYPE) === "zoom") {
        this._resizeZoomedImage({ element, id, ratio, layout, update });
        // skip other cases for this layout
        return;
      }

      const size = element.getIn([layout, side]);
      update.addPropUpdate(id, {
        name: `${layout}.${name}`,
        value: Math.round(size * ratio ** sign),
      });
    });

    this.onUpdateBlock(update);
  },

  onResizeToAspectRatio(element, elementId, aspectRatio) {
    if (aspectRatio) {
      this._resizeElement(element, elementId, aspectRatio);
    }
  },

  onUpdateTheme(data) {
    const { theme } = data;

    UndoRedoManager.save();

    this.siteDesign.theme = theme;

    this._updateDesignFiles();
    this.trigger();
  },

  onUpdateDesign(/* vo/DesignUpdate */ designUpdate) {
    UndoRedoManager.save();

    this.siteDesign.applyUpdate(designUpdate);

    this._updateDesignFiles();

    this.trigger();
  },

  /**
   * Skips a separate undo point so the accompanying element write and this
   * recents change undo together; saving here would also clear the redo stack.
   * @param {import("@/types/data/Pexels.mjs").PexelsPhoto} photo
   */
  onRecordRecentPexelsPhoto(photo) {
    if (!this.siteDesign || !photo) return;

    this.siteDesign.applyUpdate(
      buildRecentPexelsPhotosUpdate(this.siteDesign.recentPexelsPhotos, photo),
    );

    this._updateDesignFiles();

    this.trigger();
  },

  _moveBlock(blockContainer, target, item) {
    switch (target.layerType) {
      case ITEM_TYPE_BLOCK:
        blockContainer.updateBlockPosition(item.id, target.index);
        break;
      default:
    }
  },

  _moveBlockState(targetBlock, target, itemBlock, item) {
    const itemPath = IDUtils.getArray(item.id);

    switch (target.layerType) {
      case ITEM_TYPE_BLOCK:
        if (itemBlock.id == targetBlock.id) {
          // if they are dropping the state on the same block just make the state the top state
          targetBlock.updateStatePosition(itemPath, 0);
        } else {
          const removedResult = itemBlock.removeState(itemPath[2]);
          if (removedResult.index != -1) {
            targetBlock.insertStateAt(removedResult.state);
          }
        }
        break;
      case ITEM_TYPE_STATE:
        if (itemBlock.id == targetBlock.id) {
          targetBlock.updateStatePosition(itemPath, target.index);
        }
        break;
      default:
    }
  },

  /**
   * Move handler for changing element positions
   * @param {Block} targetBlock
   * @param {import("@/entities/ElementContainer.js").Target} target
   * @param {Block} itemBlock
   * @param {*} item
   * @param {Map} selected
   */
  _moveElement(targetBlock, target, itemBlock, item, selected) {
    const selectedElements = selected.get("elements");
    const isSelected = selectedElements.has(item.id);

    const targetPath = IDUtils.getArray(target.id);
    const elementPaths = [];

    if (isSelected) {
      selectedElements.forEach((elem, elemId) => {
        elementPaths.push(IDUtils.getArray(elemId));
      });
    } else {
      elementPaths.push(IDUtils.getArray(item.id));
    }

    let stateIndex = null;

    switch (target.layerType) {
      case ITEM_TYPE_BLOCK:
        if (itemBlock.id === targetBlock.id) {
          targetBlock.updateElementPositions(elementPaths); // place at end of block elements
        } else {
          itemBlock.moveElementsToBlock(elementPaths, targetBlock);
        }
        break;
      case ITEM_TYPE_STATE:
        stateIndex = targetPath[2];
        targetBlock.updateElementPositions(elementPaths, null, stateIndex);
        break;
      case ITEM_TYPE_ELEMENT:
      case ITEM_TYPE_GROUP:
        stateIndex = targetBlock.getStateIndexFromPath(targetPath);

        targetBlock.updateElementPositions(
          elementPaths,
          target.index,
          stateIndex,
          target.containedBy ? target : undefined,
          !!target.containedBy
        );

        break;
      default:
    }
  },

  _moveCanvasSetBlock({ item, target }) {
    this.siteDesign.moveCanvasSetBlock({ item, target });
    this._updateLists();
  },

  onMoveItem(item, target) {
    UndoRedoManager.save();

    this.siteDesign.moveItem(item, target);

    this._updateDesignFiles();

    this._updateLists();

    this.trigger();
  },

  onMoveLayer(data) {
    const { item, target } = data;
    const { selected } = this;

    const blockContainer = this.getBlockContainer();

    const itemPath = IDUtils.getArray(item.id);
    const targetPath = IDUtils.getArray(target.id);

    UndoRedoManager.save();

    // make sure to mark selected after save
    this._markSelected(selected, blockContainer);

    const itemBlock = blockContainer.getBlockEntityByPath(itemPath);
    // item and target are same block if canvasSet (canvasSet view)
    let targetBlock =
      item.layerType === ITEM_TYPE_CANVAS_SET_BLOCK
        ? itemBlock
        : blockContainer.getBlockEntityByPath(targetPath);

    switch (item.layerType) {
      case ITEM_TYPE_BLOCK:
        this._moveBlock(blockContainer, target, item);
        break;
      case ITEM_TYPE_STATE:
        this._moveBlockState(targetBlock, target, itemBlock, item);
        break;
      case ITEM_TYPE_ELEMENT:
      case ITEM_TYPE_GROUP:
        this._moveElement(targetBlock, target, itemBlock, item, selected);
        break;
      case ITEM_TYPE_CANVAS_SET_BLOCK:
        this._moveCanvasSetBlock(data);
        break;
      default:
    }

    // apply any changes that occurred
    itemBlock.applyToParent();
    targetBlock.applyToParent();

    // dissolve group if appropriate
    if (item.containedBy) {
      const containerPath = targetBlock.findElementContainerEntityById(item.containedBy);
      const container = targetBlock.getElementContainerEntity(containerPath);

      if (container && container.shouldDissolve()) {
        this.dissolveGroup({ id: containerPath.join(".") }, targetBlock.id, false);
        // grab updated block
        targetBlock = blockContainer.getBlockEntityByPath(targetPath);
      }
    }

    this.selected = this._refreshSelected(selected, blockContainer);

    // Make sure to remove any marked items from the data model before saving
    itemBlock.clearMarked();
    targetBlock.clearMarked();

    itemBlock.applyToParent();
    targetBlock.applyToParent();

    this._updateDesignFiles();
    this.trigger();
  },

  /**
   * Reorders every block on the current page into top-to-bottom,
   * left-to-right reading order for the given layout.
   * @param {{ layout: "desktop" | "mobile" }} payload
   */
  onSortPageByReadingOrder(payload) {
    const blocks = this.getBlockContainer().getBlocks();
    if (!blocks || blocks.size === 0) return;
    this._sortBlocksByReadingOrder(
      blocks.map((b) => b.get("id")).toArray(),
      payload.layout,
      "page"
    );
  },

  /**
   * Reorders a single block's elements into reading order.
   * @param {{ blockId: string, layout: "desktop" | "mobile" }} payload
   */
  onSortBlockByReadingOrder(payload) {
    if (!payload.blockId) return;
    this._sortBlocksByReadingOrder([payload.blockId], payload.layout, "block");
  },

  /**
   * @param {Array<string>} blockIds
   * @param {"desktop" | "mobile"} layout
   * @param {"page" | "block"} scope - analytics event metadata only
   */
  _sortBlocksByReadingOrder(blockIds, layout, scope) {
    if (!blockIds.length) return;
    const blockContainer = this.getBlockContainer();
    const { selected } = this;

    UndoRedoManager.save();
    this._markSelected(selected, blockContainer);

    let elementCount = 0;
    let touched = 0;

    blockIds.forEach((blockId) => {
      const blockEntity = blockContainer.getBlockEntityByPath([blockId]);
      if (!blockEntity) return;

      const topElements = blockEntity.getElements();
      elementCount += topElements ? topElements.size : 0;

      blockEntity.sortElementsByReadingOrder(layout);
      blockEntity.clearMarked();
      blockEntity.applyToParent();

      touched += 1;
    });

    this.selected = this._refreshSelected(selected, blockContainer);
    this._updateDesignFiles();
    this.trigger();

    UserActions.trackEvent({
      event: "layer-order-fix-applied",
      meta: { layout, scope, elementCount, blockCount: touched },
    });
  },

  onReplaceSiteBlock(payload) {
    UndoRedoManager.save();

    this.siteDesign.overwriteBlock(payload.block.id, payload.block);

    this._updateDesignFiles();
    this._updateSiteBlockList();
    this.trigger();

    SiteActions.replaceSiteBlockCompleted();
  },

  /**
   * Marks elements and blocks for selection.
   * @param {Map} selected
   * @param {BlockContainer} blockContainer
   */
  _markSelected(selected, blockContainer) {
    const blockId = selected.get("blockId", false);

    if (blockId) {
      const block = blockContainer.getBlockEntity(blockId);

      if (block) {
        const blockState = selected.get("blockState");
        let selectedState = null;
        if (blockState) {
          selectedState = IDUtils.getArray(blockState);
        }

        const elements = selected.get("elements");
        const elementPaths = [];
        elements.forEach((elem, elemId) => {
          elementPaths.push(IDUtils.getArray(elemId));
        });

        block.markAsSelected(elementPaths, selectedState);
        block.applyToParent();
      }
    }
  },

  /**
   * Takes marked items and adds them to the selection map
   * @param {Map} selected
   * @param {BlockContainer} blockContainer
   * @returns {Map}
   */
  _refreshSelected(selected, blockContainer) {
    const blockId = selected.get("blockId", null);
    let selectedState = null;
    let selectedElements = Immutable.Map();

    if (blockId) {
      const block = blockContainer.getBlockEntity(blockId);

      if (block) {
        const selectedItems = block.findSelected();

        const statePath = selectedItems.state;
        if (statePath) {
          // make sure the state belongs to the block
          if (statePath[0] == blockId) {
            selectedState = IDUtils.getPathID(statePath);
          }
        }

        const { elements } = selectedItems;

        elements.forEach((path) => {
          // make sure element belongs to the block
          if (path[0] === blockId) {
            // make sure only block elements or elements that belong to the selected state are added
            if (path[1] === "elements" || (statePath && path[2] == statePath[2])) {
              const elem = this._getSelectedElementData(block, path);
              if (elem) {
                selectedElements = selectedElements.set(elem.id, elem);
              }
            }
          }
        });
      }
    }

    selected = selected.merge({
      blockId,
      blockState: selectedState,
      elements: selectedElements,
      lastElement: null,
    });

    return selected;
  },

  /**
   * Gets the element's info.
   * @param {Block} block
   * @param {string | Array<string>} elementId
   * @returns {ElementSelectionInfo}
   */
  _getSelectedElementData(block, elementId) {
    const path = Array.isArray(elementId) ? elementId : IDUtils.getArray(elementId);
    const id = IDUtils.getPathID(path);
    const info = block.getElementInfo(path);

    let data = null;

    if (info) {
      data = { id, edit: false };
      _.merge(data, info);
    }

    return data;
  },

  /**
   * Updates the current selection data with fresh element information without changing which elements are selected
   * @param {BlockContainer} blockContainer
   */
  _updateSelectionData(blockContainer) {
    const blockId = this.selected.get("blockId");
    if (!blockId) return;

    const block = blockContainer.getBlockEntity(blockId);
    if (!block) return;

    const currentElements = this.selected.get("elements");
    if (!currentElements || currentElements.isEmpty()) return;

    let updatedElements = currentElements;
    currentElements.forEach((elementData, elementId) => {
      const freshElementData = this._getSelectedElementData(block, elementId);
      if (freshElementData) {
        updatedElements = updatedElements.set(elementId, freshElementData);
      }
    });

    this.selected = this.selected.set("elements", updatedElements);
  },

  /** Locking / Unlocking functions */
  _updateElementLockStatus(id, status) {
    const update = new BlockUpdateVO();
    update.add(id, LOCK_EDITING_KEY, status);
    this.onUpdateBlock(update);
  },

  _toggleElementLock(element, shouldLock) {
    const status = shouldLock ? "1" : null;
    const id = element.get("selected").get("id");
    this._updateElementLockStatus(id, status);
  },

  onToggleItemsLockStatus() {
    const targetElements = SelectionStore.getFullElements();
    const allLocked = targetElements.every((item) => item.get(LOCK_EDITING_KEY) === "1");
    const someLocked = targetElements.some((item) => item.get(LOCK_EDITING_KEY) === "1");
    const lockStatus = !allLocked;
    targetElements.forEach((element) => {
      if (allLocked || !someLocked || (someLocked && element.get(LOCK_EDITING_KEY) !== "1")) {
        // todo: handle this
        // if (ElementGroupUtils.isGroupParent(element)) {
        //   this.toggleElementGroupLock(element.get("id"));
        // } else {
        this._toggleElementLock(element, lockStatus);
        // }
      }
    });
  },

  // CONTAINED ELEMENT FUNCTIONS

  /**
   * Turns element selection into a group.
   * @param {Array<ElementSelectionInfo>} elementSelection
   * @param {string} blockId
   */
  groupSelection(elementSelection, blockId) {
    const blockContainer = this.getBlockContainer();
    /** @type {Block} */
    const block = blockContainer.getBlockEntity(blockId);

    // collect lowest valid state index target
    const stateIndex = elementSelection.reduce((result, current) => {
      const { stateId: id } = current;
      return id !== -1 && (result === -1 ? true : id < result) ? id : result;
    }, -1);

    const elementPaths = [...elementSelection.map(({ id }) => id).values()];
    const elementsData = elementPaths.map((id) => block.data.getIn(id.split(".").slice(1)));

    const newGroup = DesignPreparer.prepareGroup(elementsData);

    const removed = block.removeElements(elementPaths.map((id) => id.split(".")));

    let targetIndex = (
      stateIndex !== null && stateIndex !== -1
        ? block.getStateElements(stateIndex)
        : block.getElements()
    ).size;

    const groupPath = block.insertElements([newGroup], targetIndex, stateIndex);

    const added = block.insertElements(
      removed.map(({ element }) => element),
      targetIndex,
      stateIndex,
      true
    );

    if (!added.length) return;

    UndoRedoManager.save();

    this._selectNewElements(groupPath, block);

    block.applyToParent();

    this._updateDesignFiles();
    this.trigger();
  },

  /**
   * Removes all elements from a group, deletes the group, and places them where the group was.
   * @param {ElementSelectionInfo} targetGroup
   * @param {string} blockId
   */
  dissolveGroup(targetGroup, blockId, addUndo = true) {
    const blockContainer = this.getBlockContainer();
    /** @type {Block} */
    const block = blockContainer.getBlockEntity(blockId);

    const groupPath = targetGroup.id.split(".");

    const stateIndex = block.getStateIndexFromPath(groupPath);
    const groupIndex = parseInt(groupPath.at(-1));

    const container = block.getElementContainerEntity(targetGroup.id);

    const paths = container
      .getElementPaths()
      .map((path) => path.split("."))
      .toArray();

    const updates = paths.length
      ? block.updateElementPositions(paths, groupIndex + 1, stateIndex, undefined, true)
      : [];

    // don't bail out on no updates if all elements have already been removed
    if (!updates.length && paths.length) return;

    container.delete();

    addUndo && UndoRedoManager.save();

    // shifts ids back starting at where the group previously was
    const newIds = Array.from({ length: updates.length }).map((_, index) =>
      [...groupPath.slice(0, -1), groupIndex + index].join(".")
    );

    this._selectNewElements(newIds, block);

    block.applyToParent();

    this._updateDesignFiles();
    this.trigger();
  },

  /**
   * Adds an element to the group at an index if provided
   * @param {ElementSelectionInfo} targetGroup
   * @param {Array<ElementSelectionInfo>} elementSelection
   * @param {string} blockId
   * @param {number=} insertAt - Index to insert at within the group. Defaults to the top.
   */
  addToGroup(targetGroup, elementSelection, blockId, insertAt) {
    const elementsToAdd = [...elementSelection].toReversed();

    const blockContainer = this.getBlockContainer();
    /** @type {Block} */
    const block = blockContainer.getBlockEntity(blockId);

    const elementPaths = elementsToAdd.map(({ id }) => IDUtils.getArray(id));
    const splitGroupId = targetGroup.id.split(".");

    const stateIndex = block.getStateIndexFromPath(splitGroupId);
    const groupIndex = parseInt(splitGroupId.at(-1));

    const updates = block.updateElementPositions(elementPaths, groupIndex, stateIndex, true, true);

    if (!updates.length) return;

    UndoRedoManager.save();

    this._selectNewElements(
      updates.map((update) => update.new),
      block
    );

    block.applyToParent();

    this._updateDesignFiles();
    this.trigger();
  },

  /**
   * Adds an element to the group at an index if provided
   * @param {Array<ElementSelectionInfo>} elementSelection
   * @param {string} blockId
   * @param {number=} insertAt - Index to insert at outside of the group. Defaults to the top.
   */
  removeFromGroup(elementSelection, blockId, insertAt = 0) {
    const elementsToAdd = [...elementSelection];

    const blockContainer = this.getBlockContainer();
    /** @type {Block} */
    const block = blockContainer.getBlockEntity(blockId);

    const elementPaths = elementsToAdd.map(({ id }) => id.split("."));
    const stateIndex = block.getStateIndexFromPath(elementPaths[0]);
    const groupPath = IDUtils.getElementContainerFromPath(elementPaths[0]);
    const groupIndex = parseInt(groupPath.at(-1));

    const container = block.getElementContainerEntity(groupPath);

    // if second to last element is being removed, just dissolve the group
    if (container.shouldDissolve(elementsToAdd.length)) {
      return this.dissolveGroup({ id: groupPath.join(".") }, blockId);
    }

    const updates = block.updateElementPositions(
      elementPaths,
      groupIndex + 1,
      stateIndex,
      false,
      true
    );

    if (!updates.length) return;

    UndoRedoManager.save();

    this._selectNewElements(
      updates.map((update) => update.new),
      block
    );

    block.applyToParent();

    this._updateDesignFiles();
    this.trigger();
  },

  /**
   * Resizes the group by id. Assumes current block as parent.
   * @param {string} idOrPath
   */
  onResizeGroup(idOrPath) {
    if (!idOrPath) return;

    const blockContainer = this.getBlockContainer();
    /** @type {Block} */
    const block = blockContainer.getBlockEntity(this.selected.get("blockId"));

    const splitPath = IDUtils.getArray(idOrPath);

    const pathToContainer = (
      splitPath.length === 1 ? block.findElementContainerEntityById(splitPath[0]) : splitPath
    )?.slice(1);
    const container = block.getElementContainerEntity(pathToContainer);

    const before = container._getOwnPositions();
    container.resize();
    const after = container._getOwnPositions();

    // don't update if there's no change
    if (!_.isEqual(before, after)) {
      block.applyToParent();

      // update focus display if focused
      if (GroupStore.isFocused()) {
        const layout = this.selected.get("layout");
        GroupActions.updateGroupData(after[layout], layout);
      }

      this._updateDesignFiles();
      this.trigger();
    }
  },

  // UTIL FUNCTIONS

  _isBlockContainerType(type) {
    return this.blockContainerType === type;
  },

  /**
   * Gets element data from a block by path
   * @param {string} path - The path to the element, can be a dot notation string or array
   * @param {string} [targetBlockId] - Optional block ID to get element from. If not provided, uses currently selected block
   * @returns {Map} The element data as an Immutable Map
   */
  getElementData(path, targetBlockId) {
    const blockId = targetBlockId || this.selected.get("blockId");
    const blockContainer = this.getBlockContainer();
    const block = blockContainer.getBlockEntity(blockId);
    const pathArray = IDUtils.getArray(path);

    return block.getElement(pathArray);
  },

  /**
   * Collects the currently selected elements data
   * @param {Object} props
   * @param {boolean=} props.asJS - Return the selected elements as an oject instead of an immutable map
   * @returns {Record<string, Object> | Map}
   */
  findSelectedElements({ asJS = false } = {}) {
    const blockId = this.selected.get("blockId");
    const selected = [];

    if (blockId != null) {
      const elements = this.selected.get("elements");
      const blockContainer = this.getBlockContainer();
      const block = blockContainer.getBlockEntity(blockId);

      if (block && elements.size > 0) {
        const ids = _.map(elements.toJS(), "id");
        let paths = _.map(ids, (id) => IDUtils.getArray(id));
        const containerPath = IDUtils.getElementContainerFromPath(paths[0]);

        const elementContainer = containerPath
          ? block.getElementContainerEntity(containerPath)
          : null;

        paths = elementContainer
          ? elementContainer.reorderPaths(paths)
          : block._sortElementPaths(paths);

        paths = paths.reverse();

        paths.forEach((path) => {
          const elem = block.getElement(path);
          if (elem) {
            if (asJS) {
              selected.push(elem.toJS());
            } else {
              selected.push(elem);
            }
          }
        });
      }
    }

    return selected;
  },

  onAddGlobalCode(embed) {
    // Already at least one custom code embed
    if (this.siteDesign.data.get(GLOBAL_CODE_DATA_KEY)) {
      this.siteDesign.data = this.siteDesign.data.setIn(
        [GLOBAL_CODE_DATA_KEY, embed.id],
        Immutable.fromJS(embed)
      );
      const globalCode = this.siteDesign.data.get(GLOBAL_CODE_KEY).push(embed.id);
      this.siteDesign.data = this.siteDesign.data.set(GLOBAL_CODE_KEY, globalCode);
    } else {
      // No custom code embeds yet
      this.siteDesign.data = this.siteDesign.data.set(
        GLOBAL_CODE_DATA_KEY,
        Immutable.fromJS({ [embed.id]: embed })
      );
      this.siteDesign.data = this.siteDesign.data.set(
        GLOBAL_CODE_KEY,
        Immutable.fromJS([embed.id])
      );
    }

    this.trigger(this.siteDesign.data);
  },

  onUpdateGlobalCode(embed) {
    const update = new DesignUpdate();
    update.add([GLOBAL_CODE_DATA_KEY, embed.get("id")], embed, AppConstants.UPDATE_ACTION_SET);
    SiteActions.updateDesign(update);

    this.trigger(this.siteDesign.data);
  },

  onRemoveGlobalCode(id) {
    const updatedGlobalCode = this.siteDesign.data.update(GLOBAL_CODE_KEY, (list) =>
      list.filter((codeId) => codeId !== id)
    );

    const update = new DesignUpdate();
    update.add([GLOBAL_CODE_DATA_KEY, id], null, AppConstants.UPDATE_ACTION_DELETE);
    update.add(
      GLOBAL_CODE_KEY,
      updatedGlobalCode.get(GLOBAL_CODE_KEY),
      AppConstants.UPDATE_ACTION_SET
    );
    SiteActions.updateDesign(update);
    this.trigger(this.siteDesign.data);
  },

  getGlobalCode() {
    return {
      [GLOBAL_CODE_KEY]: this.siteDesign.data.get(GLOBAL_CODE_KEY, Immutable.Map()),
      [GLOBAL_CODE_DATA_KEY]: this.siteDesign.data.get(GLOBAL_CODE_DATA_KEY, Immutable.Map()),
    };
  },

  onAddCodePageException(pageId, includeInGlobalCode) {
    const update = new DesignUpdate();
    if (!includeInGlobalCode) {
      update.add(
        [AppConstants.ITEM_TYPE_SITE_ITEMS, pageId, CANVAS_SET_DATA, EXCLUDE_FROM_GLOBAL_CODE],
        true,
        AppConstants.UPDATE_ACTION_SET
      );
    } else {
      update.add(
        [AppConstants.ITEM_TYPE_SITE_ITEMS, pageId, CANVAS_SET_DATA],
        null,
        AppConstants.UPDATE_ACTION_DELETE
      );
    }
    SiteActions.updateDesign(update);
    this.trigger(this.siteDesign.data);
  },

  // UTIL FUNCTIONS END

  /**
   * Returns either the SiteDesign or SitePage entity
   * @returns {typeof SitePage | typeof SiteDesign}
   */
  getBlockContainer() {
    return this.blockContainerType === SITE_ITEM_SITE ? this.siteDesign : this.sitePage;
  },

  /**
   * @return SitePage
   */
  getCurrentPage() {
    return this.sitePage;
  },

  /**
   * @return SiteDesign
   */
  getDesign() {
    return this.siteDesign;
  },

  getFullPageList() {
    return this.fullPageList;
  },

  getPageList() {
    return this.pageList;
  },

  getPageIds() {
    return this.pageList.map((page) => page.get("id"));
  },

  getSiteBlockList() {
    return this.siteBlockList;
  },

  getCanvasSetList() {
    return this.canvasSetList;
  },

  findCanvasSet(id) {
    return this.siteDesign.getCanvasSet({ id });
  },

  isPartOfCanvasSet(blockId) {
    if (blockId) {
      const page = this.sitePage?.data;
      const design = this.siteDesign;
      const activeItemHistoryBase =
        DesignViewStore.getActiveItemHistory()?.get(ITEM_HISTORY_TYPE_INDEX);

      if (
        activeItemHistoryBase &&
        activeItemHistoryBase.get(ITEM_HISTORY_TYPE_INDEX) === ITEM_TYPE_CANVAS_SET
      ) {
        return design.getCanvasSet({ id: activeItemHistoryBase.get(ITEM_HISTORY_ID_INDEX) });
      }
      const canvasSet = design.getCanvasSetByChildBlockAndPage(page.get("id"), blockId);
      return canvasSet;
    }
    return false;
  },

  getSiteBlockIds() {
    return this.siteBlockList.map((siteBlock) => siteBlock.get("id"));
  },

  getSelected() {
    return this.selected;
  },

  /**
   * The block a new element goes into when no block is given: the selected block, else the first.
   * @returns {string} block id
   */
  getAddElementBlockId() {
    return this.selected.get("blockId") ?? this.getBlockContainer().getBlockID(0);
  },

  /**
   * Returns block entity with provided id
   * @param {string} blockId
   * @returns {Block}
   */
  getBlock(blockId) {
    return this.getBlockContainer().getBlock(blockId);
  },

  _resetSelection() {
    this.selected = Immutable.fromJS(selectedModel);
  },

  _isInitComplete() {
    return this.initPage;
  },

  _updateLists() {
    this._updatePageList();
    this._updateSiteBlockList();
    this._updateCanvasSetList();
    SiteActions.siteListsUpdated();
  },

  /**
   * Signal design manager to update a file with data from siteDesign
   * @todo remove forceInSync param
   * @param {boolean} forceInSync - Update files without triggering immediate remote save. Unused as far as I can tell.
   */
  _updateDesignFiles(forceInSync) {
    if (this.siteDesign) {
      DesignManager.updateFile(this.siteDesign.data, forceInSync);
    }

    if (this.sitePage) {
      DesignManager.updateFile(this.sitePage.data, forceInSync);
    }
  },

  _updatePageList() {
    this.pageList = this.siteDesign.getPageList({ withFolders: false });
    this.fullPageList = this.siteDesign.getPageList();
  },

  _updateSiteBlockList() {
    this.siteBlockList = this.siteDesign.getBlockList();
  },

  _updateCanvasSetList() {
    const canvasSetsList = this.siteDesign.getCanvasSetsList();
    const canvasSets = this.siteDesign.getCanvasSets();

    this.canvasSetList = canvasSetsList.map((id) => canvasSets.get(id));
  },

  getCurrentState() {
    return {
      selected: this.selected,
      designData: this.siteDesign.data,
      pageData: this.sitePage.data,
      blockContainerType: this.blockContainerType,
    };
  },

  applyState(data) {
    this.selected = data.selected;
    this.siteDesign.data = data.designData;
    this.sitePage.data = data.pageData;
    this.blockContainerType = data.blockContainerType;

    this._updateDesignFiles();

    this._updateLists();

    this.trigger();
  },

  onUndo() {
    if (UndoRedoManager.canUndo()) {
      UndoRedoManager.undo();
    }
  },

  onRedo() {
    if (UndoRedoManager.canRedo()) {
      UndoRedoManager.redo();
    }
  },

  reset() {
    // Clear pending page tracking when store is reset
    if (this.pagesPendingSave) {
      this.pagesPendingSave.clear();
    }
  },
});

export default SiteDesignStore;
